import BattleEngine from 'dt-common/battle_engine/BattleEngine';
import LootSpawnEvent from 'dt-common/battle_engine/events/LootSpawnEvent';
import serializableObj from 'dt-common/isomorphic-helpers/serializableObj';
import mutationCavernsUpdateUnits from '../graphql/caverns/mutationCavernsUpdateUnits';
import mutationSpawnCavernsMob from '../graphql/caverns/mutationSpawnCavernsMob';
import { createMobDeathBatcher } from './mobDeathBatcher';
import { ApiErrorMessage } from 'dt-common/types/ApiErrorMessages';
import { GraphQlRequestError } from '~/graphql/graphqlClient';
import BattleCalc from 'dt-common/battle_engine/BattleCalc';

export const CAVERNS_BATTLE_INIT_DELAY = 800;

let _battle_engine;
let _updateUnitsInFlight = false;

const mobDeathBatcher = createMobDeathBatcher({
  getPlayerId: () => _battle_engine?.whitePlayerId,
  getGameSubmode: () => _battle_engine?.game_submode,
  onLootEvents: (events) => {
    for (const { deathTileId, death_tile_loot } of events) {
      new LootSpawnEvent(_battle_engine.battleState).execute(deathTileId, death_tile_loot);
    }
  },
  onDeathRegistered: (num_remaining_mobs: number) => {
    if (num_remaining_mobs === 0) {
      postMessage({ message_name: 'room_is_clear' });
    }
  },
  debounceMs: 700,
});

onmessage = function (event) {
  if (typeof message_handlers[event.data.message_name] === 'function') {
    message_handlers[event.data.message_name](event.data.message_data);
  }
};

const message_handlers = {
  caverns_travel_success: ({ new_caverns_level, new_room, all_units_array, initial_mobs }) => {
    _battle_engine.current_caverns_level = new_caverns_level;
    _battle_engine.setBattleId(`caverns-${new_caverns_level}-${_battle_engine.whitePlayerId}`);

    _battle_engine.clearTimeouts();
    _battle_engine.battleState.onCavernsTravel({
      new_room,
      all_units_array,
    });
    for (const mob of initial_mobs) {
      _battle_engine.battleState.registerNewUnit(mob);
    }
    _battle_engine.remakeDecisionTrees();
    _battle_engine.enableUnitActions();
    _battle_engine.resume();

    const { allPieces, allTiles, allUnits, doors, walls, portal } = _battle_engine.battleState;
    postMessage({
      message_name: 'caverns_level_initialized',
      message_data: { caverns_level: new_caverns_level },
      battle_state: serializableObj({ allPieces, allTiles, allUnits, doors, walls, portal }),
    });
  },

  pause: () => {
    _battle_engine.pause();
  },

  disable_unit_actions: () => {
    _battle_engine.disableUnitActions();
  },

  enable_unit_actions: () => {
    _battle_engine.enableUnitActions();
  },

  start_battle_engine: (message_data) => {
    _battle_engine = new BattleEngine(message_data.battle_engine_constructor_arg, {
      logError: console.error, // TODO: why can't we just use logError from logger?
    });

    _battle_engine.eventEmitter.on('spawn_new_enemy', async () => {
      try {
        // Find unoccupied tile adjacent to doors using fresh battleState
        const { allTiles, doors } = _battle_engine.battleState;
        const unoccupied_tiles = Object.values(allTiles).filter((tile) => !tile.occupied);
        const spawn_tile_candidates = [];

        for (const tile of unoccupied_tiles) {
          const doorArray = Array.isArray(doors) ? doors : Object.values(doors || {});
          for (const door of doorArray) {
            if (BattleCalc.adjacent(door, tile)) {
              spawn_tile_candidates.push(tile);
              break;
            }
          }
        }

        if (!spawn_tile_candidates.length) {
          return;
        }

        const spawn_tile = spawn_tile_candidates[Math.floor(Math.random() * spawn_tile_candidates.length)];

        // Request mob spawn from API with the chosen coordinates
        const res = await mutationSpawnCavernsMob({
          playerId: _battle_engine.whitePlayerId,
          game_submode: _battle_engine.game_submode,
          spawnX: spawn_tile.x,
          spawnY: spawn_tile.y,
        });

        // Only register unit if API call succeeded
        if (res?.mob) {
          _battle_engine.battleState.registerNewUnit(res.mob);
        }
      } catch (err) {
        if (err instanceof GraphQlRequestError && err.errors.find((error) => error.message === ApiErrorMessage.NO_CURRENT_RUN_FOUND)) {
          postMessage({ message_name: 'missing_caverns_run_data' });
          return;
        }
      }
    });

    _battle_engine.eventEmitter.on('statCostsExacted', bubbleMessage.bind(null, 'statCostsExacted'));
    _battle_engine.eventEmitter.on(
      'battleEngineError',
      bubbleMessage.bind(null, 'battleEngineError')
    );
    _battle_engine.eventEmitter.on('aiTurn', bubbleMessage.bind(null, 'aiTurn'));
    _battle_engine.eventEmitter.on('abilityExecuted', bubbleMessage.bind(null, 'abilityExecuted'));
    _battle_engine.eventEmitter.on('gotUnitState', bubbleMessage.bind(null, 'gotUnitState'));
    _battle_engine.eventEmitter.on('battleEvent', bubbleMessage.bind(null, 'battleEvent'));
    _battle_engine.eventEmitter.on('unitCondition', bubbleMessage.bind(null, 'unitCondition'));
    _battle_engine.eventEmitter.on(
      'unitConditionExpired',
      bubbleMessage.bind(null, 'unitConditionExpired')
    );
    _battle_engine.current_caverns_level = message_data.new_caverns_data.startingLevel;

    setTimeout(() => {
      _battle_engine?.init();
      _battle_engine?.resume();
    },
      CAVERNS_BATTLE_INIT_DELAY
    );

    for (const mob of message_data.new_caverns_data.spawned_mobs) {
      _battle_engine.battleState.registerNewUnit(mob);
    }

    const { allPieces, allTiles, allUnits, doors, walls, portal } = _battle_engine.battleState;
    postMessage({
      message_name: 'caverns_level_initialized',
      message_data: {
        caverns_level: message_data.new_caverns_data.startingLevel,
      },
      battle_state: serializableObj({ allPieces, allTiles, allUnits, doors, walls, portal }),
    });

    // constantly emit unit stats (from StatRecovery)
    setInterval(() => {
      if (!_battle_engine.battleState.paused)
        postMessage({
          message_name: 'unit_stats',
          battle_state: serializableObj({
            allUnits: _battle_engine.battleState.allUnits,
          }),
        });
    }, 30);

    // sync white_units, mobs, and tile occupation to caverns_run every 7 seconds
    setInterval(() => {
      const { allUnits, allTiles } = _battle_engine.battleState;
      if (allUnits && allTiles && !_updateUnitsInFlight) {
        _updateUnitsInFlight = true;
        mutationCavernsUpdateUnits({
          playerId: _battle_engine.whitePlayerId,
          game_submode: _battle_engine.game_submode,
          allUnits: serializableObj(allUnits),
          allTiles: serializableObj(allTiles),
        }).catch((err) => {
          if (err instanceof GraphQlRequestError && err.errors.find((error) => error.message === ApiErrorMessage.NO_CURRENT_RUN_FOUND)) {
            postMessage({ message_name: 'missing_caverns_run_data' });
            return;
          }
        }).finally(() => {
          _updateUnitsInFlight = false;
        });
      }
    }, 7000);
  },

  tile_looted: (message_data) => {
    // Backwards compatible: accept either a single tile id or a list
    const { single_tile_looted_id, tile_looted_ids } = message_data || {};

    const ids: string[] = [];
    if (Array.isArray(tile_looted_ids)) {
      ids.push(...tile_looted_ids);
    } else if (single_tile_looted_id) {
      ids.push(single_tile_looted_id);
    }

    for (const id of ids) {
      const tile = _battle_engine.battleState.allTiles[id];
      if (tile) tile.loot = null;
    }
  },

  cleanup: () => {
    _battle_engine.dispose();

    postMessage({
      message_name: 'cleaned_up',
    });
  },
};

const bubbleMessage = async (message_name, message) => {
  message = serializableObj(message);
  try {
    // in some cases we need to perform some business logic before bubbling the message...
    switch (message_name) {
      case 'battleEvent':
        {
          const { event } = message;
          processAffectedPieces(event.affectedPieces);
          const { allUnits, allPieces, livingHeroHandles = [], allTiles } = _battle_engine.battleState;
          const existing_unit = allUnits[event.actorId];
          const unit = existing_unit || event.unit;
          if (!unit || event.eventHandle === 'death') {
            // remove from battleState dicts
            delete allUnits[event.actorId];
            delete allPieces[event.actorId];
            // recalculate living heroes
            livingHeroHandles.length = 0;
            for (const u of Object.values(allUnits || {})) {
              if (u.hero && u.team === 'white' && !u.dead && u.handle !== 'poppet') {
                livingHeroHandles.push(u.hero_handle || u.handle);
              }
            }
            _battle_engine.battleState.livingHeroHandles = livingHeroHandles;

            // check if the party has been defeated
            if (unit?.team === 'white' && unit?.hero && livingHeroHandles.length === 0) {
              _battle_engine.disableUnitActions();
              _battle_engine.pause();
              postMessage({
                message_name: 'party_defeated',
                message_data: {},
                battle_state: serializableObj({ allPieces, allUnits, allTiles }),
              });
            }

            // report mob death (debounced/batched)
            if (
              unit?.team === 'black'
            ) {
              mobDeathBatcher.queue({
                mob_id: unit.uid,
                deathTileId: event.deathTile.uid,
                livingHeroHandles,
              });
            }
          }
        }
        break;

      case 'unitCondition':
        {
          const condition = message;
          const owner = _battle_engine.battleState.allPieces[condition.ownerId];
          if (!owner) {
            return;
          }

          if (!owner.conditions) {
            owner.conditions = {};
          }
          owner.conditions[condition.uid] = condition;
        }
        break;
    }

    // do the bubble, baby
    const { allPieces, allUnits, allTiles } = _battle_engine.battleState;
    postMessage({
      message_name,
      message_data: message,
      battle_state: serializableObj({ allPieces, allUnits, allTiles }),
    });

    // in some cases there is processing to do after bubbling the message
    switch (message_name) {
      case 'unitConditionExpired':
        if (_battle_engine.battleState.allUnits[message.ownerId]) {
          delete _battle_engine.battleState.allUnits[message.ownerId].conditions[message.conditionId];
        }
        break;
    }
  } catch (err) {
    console.error(err); // TODO: why can't we just use logError from logger?
  }
};

function processAffectedPieces(affectedPieces) {
  if (!affectedPieces || !_battle_engine.battleState?.allPieces) {
    return;
  }

  if (!Array.isArray(affectedPieces) && typeof affectedPieces === 'object') {
    affectedPieces = Object.values(affectedPieces);
  }

  const { allPieces, allUnits, allTiles } = _battle_engine.battleState;

  for (const piece of affectedPieces) {
    if (piece) {
      const updated_piece = Object.assign(allPieces[piece.uid] || {}, piece);

      if (piece.unit) {
        allUnits[piece.uid] = updated_piece;
      }
      if (piece.tile) {
        allTiles[piece.uid] = updated_piece;
      }
    }
  }
}


